// Lead status system — mirrors the sales "دليل أكواد حالات الطلبات" code guide.
// Enum values are DB-safe identifiers (C_01, R_00, …); STATUS_CODES maps them
// back to the human "C-01"/"R-00" codes the sales team uses.

export const LEAD_STATUSES = [
	"NEW",
	"C_01",
	"C_02",
	"I_01",
	"I_02",
	"N_01",
	"N_02",
	"N_03",
	"N_04",
	"P_01",
	"R_00",
	"R_01",
	"R_02",
	"R_03",
	"R_04",
	"R_05",
	"R_06",
	"U_01",
	"U_02",
] as const;

export type LeadStatus = (typeof LEAD_STATUSES)[number];

/** An order that went through: confirmed, with or without payment. */
export const CONFIRMED_STATUSES = [
	"C_01",
	"C_02",
] as const satisfies readonly LeadStatus[];

/**
 * Every status change names the run the order is about, whatever the status —
 * so each edition's roster, and everything counted per edition, is always
 * current. The edition already on the order carries over as the default, so
 * only the first change actually asks for one.
 *
 * The single way out is a training with no editions yet: there would be
 * nothing to pick, so the change goes through without one. Both the order
 * window and `changeStatus` ask this, so they can't disagree.
 */
export function editionMissing(change: {
	statusChanged: boolean;
	editionId: number | null;
	trainingHasEditions: boolean;
}) {
	return (
		change.statusChanged &&
		change.editionId === null &&
		change.trainingHasEditions
	);
}

// The short code shown to the sales team (matches the printed guide).
export const STATUS_CODES: Record<LeadStatus, string> = {
	NEW: "—",
	C_01: "C-01",
	C_02: "C-02",
	I_01: "I-01",
	I_02: "I-02",
	N_01: "N-01",
	N_02: "N-02",
	N_03: "N-03",
	N_04: "N-04",
	P_01: "P-01",
	R_00: "R-00",
	R_01: "R-01",
	R_02: "R-02",
	R_03: "R-03",
	R_04: "R-04",
	R_05: "R-05",
	R_06: "R-06",
	U_01: "U-01",
	U_02: "U-02",
};

export const STATUS_LABELS: Record<LeadStatus, string> = {
	NEW: "جديد",
	C_01: "مؤكدة بدون دفع",
	C_02: "مؤكدة مع دفع",
	I_01: "مهتم لم يسجّل",
	I_02: "غير مهتم تماماً",
	N_01: "لم يرد على الاتصال",
	N_02: "طلب الاتصال لاحقاً",
	N_03: "رقم خاطئ",
	N_04: "استفسار فقط",
	P_01: "مؤجلة بسبب الوقت",
	R_00: "مرفوضة تماماً",
	R_01: "مرفوضة — السعر",
	R_02: "مرفوضة — الوقت",
	R_03: "مرفوضة — محتوى الدورة",
	R_04: "مرفوضة — المدرب",
	R_05: "مرفوضة — الصيغة غير متاحة",
	R_06: "مرفوضة — لا توجد مجموعة مناسبة",
	U_01: "دورة أخرى (Up-Sell)",
	U_02: "أفراد آخرين (Up-Sell)",
};

export const STATUS_STYLES: Record<LeadStatus, string> = {
	NEW: "bg-teal-50 text-teal-700",
	C_01: "bg-green-100 text-green-800",
	C_02: "bg-green-100 text-green-800",
	I_01: "bg-blue-100 text-blue-800",
	I_02: "bg-blue-100 text-blue-800",
	N_01: "bg-slate-100 text-slate-700",
	N_02: "bg-slate-100 text-slate-700",
	N_03: "bg-slate-100 text-slate-700",
	N_04: "bg-slate-100 text-slate-700",
	P_01: "bg-amber-100 text-amber-800",
	R_00: "bg-red-100 text-red-700",
	R_01: "bg-red-100 text-red-700",
	R_02: "bg-red-100 text-red-700",
	R_03: "bg-red-100 text-red-700",
	R_04: "bg-red-100 text-red-700",
	R_05: "bg-red-100 text-red-700",
	R_06: "bg-red-100 text-red-700",
	U_01: "bg-purple-100 text-purple-800",
	U_02: "bg-purple-100 text-purple-800",
};

// Grouped by the "الفئة" column, in the order sales scans them.
export const STATUS_CATEGORIES: Array<{
	key: string;
	label: string;
	statuses: LeadStatus[];
}> = [
	{ key: "NEW", label: "جديد", statuses: ["NEW"] },
	{ key: "C", label: "تأكيد", statuses: ["C_01", "C_02"] },
	{ key: "I", label: "اهتمام", statuses: ["I_01", "I_02"] },
	{ key: "N", label: "اتصال", statuses: ["N_01", "N_02", "N_03", "N_04"] },
	{ key: "P", label: "تأجيل", statuses: ["P_01"] },
	{
		key: "R",
		label: "رفض",
		statuses: ["R_00", "R_01", "R_02", "R_03", "R_04", "R_05", "R_06"],
	},
	{ key: "U", label: "توسيع", statuses: ["U_01", "U_02"] },
];
